/**
 * Deterministic fallback template recommender.
 *
 * Picks a template ID + human-readable reason based on the user's job title,
 * years of experience, and (optionally) a parsed work-experience list.
 * This is a client-side fallback used when AI has not supplied a recommendation.
 */

export type RecommendedTemplateId =
  | "executive"
  | "ats-optimized"
  | "professional-classic";

export interface TemplateRecommendation {
  templateId: RecommendedTemplateId;
  reason: string;
}

export interface RecommendInput {
  jobTitle?: string | null;
  yearsExperience?: number | null;
  workExperience?: Array<{
    jobTitle?: string;
    position?: string;
    duration?: string;
    startYear?: string | number;
    endYear?: string | number;
  }>;
}

const SENIOR_KEYWORDS = [
  "vp",
  "vice president",
  "chief",
  "cto",
  "ceo",
  "cfo",
  "coo",
  "cmo",
  "director",
  "head of",
  "principal",
  "partner",
  "founder",
  "president",
  "executive",
];

const JUNIOR_KEYWORDS = [
  "intern",
  "junior",
  "entry",
  "graduate",
  "trainee",
  "apprentice",
  "student",
  "assistant",
];

const TECH_KEYWORDS = [
  "engineer",
  "developer",
  "programmer",
  "data scientist",
  "data analyst",
  "machine learning",
  "ml engineer",
  "devops",
  "sre",
  "qa",
  "tester",
  "designer",
  "ux",
  "ui",
  "analyst",
];

function lower(s?: string | null): string {
  return (s || "").toLowerCase();
}

function inferYearsFromWork(
  work: RecommendInput["workExperience"]
): number | null {
  if (!work || work.length === 0) return null;
  const currentYear = new Date().getFullYear();
  let total = 0;
  for (const w of work) {
    const startStr = String(w.startYear ?? "").match(/\d{4}/)?.[0];
    const endStr =
      String(w.endYear ?? "").toLowerCase().includes("present")
        ? String(currentYear)
        : String(w.endYear ?? "").match(/\d{4}/)?.[0];
    const start = startStr ? Number(startStr) : null;
    const end = endStr ? Number(endStr) : currentYear;
    if (start && end && end >= start) total += end - start;
  }
  return total > 0 ? total : null;
}

export function recommendTemplate(
  input: RecommendInput
): TemplateRecommendation {
  const title = lower(input.jobTitle);
  const years =
    input.yearsExperience ?? inferYearsFromWork(input.workExperience) ?? 0;

  const isSenior = SENIOR_KEYWORDS.some((k) => title.includes(k));
  const isJunior = JUNIOR_KEYWORDS.some((k) => title.includes(k));
  const isTech = TECH_KEYWORDS.some((k) => title.includes(k));

  if (isSenior || years >= 10) {
    return {
      templateId: "executive",
      reason: "Recommended for senior leadership profiles",
    };
  }

  if (isJunior || years < 1) {
    return {
      templateId: "ats-optimized",
      reason: "Best for early-career roles - clean and ATS-friendly",
    };
  }

  if (isTech) {
    return {
      templateId: "professional-classic",
      reason: "Balanced layout for technical experience, projects, and skills",
    };
  }

  return {
    templateId: "professional-classic",
    reason: "Best for experienced professionals",
  };
}
